import React from 'react';
import {
  FileSpreadsheet, FileType, FileText,
} from 'lucide-react';

export const CHUNK_SIZE = 2 * 1024 * 1024; // 2 MB

export const SUPPORTED_EXTS = ['pdf', 'docx', 'doc', 'txt', 'md', 'xlsx', 'xls', 'html', 'htm'];
export const SUPPORTED_MIMES = '.pdf,.docx,.doc,.txt,.md,.xlsx,.xls,.html,.htm';

export const fileIcon = (filename) => {
  const ext = (filename?.split('.').pop() || '').toLowerCase();
  if (['xlsx', 'xls'].includes(ext)) return FileSpreadsheet;
  if (['txt', 'md', 'html', 'htm'].includes(ext)) return FileType;
  return FileText;
};

export const formatBytes = (b) => {
  if (!b && b !== 0) return '—';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
};

// Markdown components used in chunk text + summary rendering across library views.
export const mdComponents = {
  h1: ({ children }) => <h1 className="text-lg font-bold text-[#0F172A] mt-3 mb-2">{children}</h1>,
  h2: ({ children }) => <h2 className="text-base font-bold text-[#1E3A8A] mt-3 mb-1.5">{children}</h2>,
  p: ({ children }) => <p className="text-sm text-slate-700 leading-relaxed mb-2">{children}</p>,
  strong: ({ children }) => <strong className="font-semibold text-slate-900">{children}</strong>,
  ul: ({ children }) => <ul className="list-disc pl-5 mb-2 space-y-0.5">{children}</ul>,
  ol: ({ children }) => <ol className="list-decimal pl-5 mb-2 space-y-0.5">{children}</ol>,
  li: ({ children }) => <li className="text-sm text-slate-700">{children}</li>,
};

// Skip rules (in order of check)
export const SKIP_REASONS = {
  EMPTY: 'Empty (0 bytes) — likely an iCloud placeholder',
  ICLOUD: 'iCloud placeholder file (not downloaded)',
  DOTFILE: 'System/dotfile (.DS_Store, etc.)',
  UNSUPPORTED: 'Unsupported file type',
  OVERSIZE: 'Exceeds 50 MB per-file limit',
};

export const evaluateFile = (file) => {
  const name = file.name;
  if (name.startsWith('.') || name === '.DS_Store' || name.toLowerCase() === '.localized') {
    return { skip: true, reason: SKIP_REASONS.DOTFILE };
  }
  if (name.toLowerCase().endsWith('.icloud')) {
    return { skip: true, reason: SKIP_REASONS.ICLOUD };
  }
  if (file.size === 0) {
    return { skip: true, reason: SKIP_REASONS.EMPTY };
  }
  const ext = (name.split('.').pop() || '').toLowerCase();
  if (!SUPPORTED_EXTS.includes(ext)) {
    return { skip: true, reason: SKIP_REASONS.UNSUPPORTED };
  }
  if (file.size > 50 * 1024 * 1024) {
    return { skip: true, reason: SKIP_REASONS.OVERSIZE };
  }
  return { skip: false };
};

// Recursive walk of a DataTransferItem entry (drag-drop folder traversal).
// Returns flat array of { file, folderTags, relativePath }.
export const walkEntry = async (entry, pathParts = []) => {
  if (entry.isFile) {
    return new Promise((resolve) => {
      entry.file((file) => {
        const folder = pathParts.slice(0, -1);
        resolve([{ file, folderTags: folder, relativePath: [...pathParts, file.name].join('/') }]);
      });
    });
  }
  if (entry.isDirectory) {
    const reader = entry.createReader();
    const results = [];
    // readEntries returns batches of 100 — loop until empty
    while (true) {
      // eslint-disable-next-line no-await-in-loop
      const entries = await new Promise((resolve, reject) => reader.readEntries(resolve, reject));
      if (entries.length === 0) break;
      for (const child of entries) {
        // eslint-disable-next-line no-await-in-loop
        const sub = await walkEntry(child, [...pathParts, entry.name]);
        results.push(...sub);
      }
    }
    return results;
  }
  return [];
};
